import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Alert,
    Typography,
    TextField,
    LinearProgress,
    Paper,
    IconButton,
    InputAdornment,
    Pagination,
    PaginationItem,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { BasicPage } from "../components/BasicPage";
import { ResultViewTypesense } from "../components/ResultView";
import { apiFetch } from "../hooks/useAuth";
import { formatISBN, isbnDigits } from "../utils/isbn";

import "@elastic/react-search-ui-views/lib/styles/styles.css";

const QUERY_BY = "title,authors,publisher,isbn";
const QUERY_BY_WEIGHTS = "10,8,2,3";
const FACET_BY = "lang,publisher,format,catalog,authors";
const MAX_FACET_VALUES = 50;

const FACET_LABELS = {
    lang: "Sprache",
    publisher: "Verlag",
    format: "Format",
    catalog: "Katalog",
    authors: "Autor(en)",
};
const FACET_ORDER = ["catalog", "lang", "publisher", "format", "authors"];
const DEFAULT_FACET_SELECTION = { catalog: ["vlb"] };
const CATALOG_VALUE_LABELS = {
    vlb: "VLB",
    libri: "Libri",
    newbooks: "Newbooks",
    magazines: "Zeitschriften",
};
const LANGUAGE_VALUE_LABELS = {
    ger: "Deutsch",
    eng: "Englisch",
    fre: "Französisch",
    ita: "Italienisch",
    spa: "Spanisch",
    por: "Portugiesisch",
    rus: "Russisch",
    pol: "Polnisch",
    dut: "Niederländisch",
    swe: "Schwedisch",
    fin: "Finnisch",
    dan: "Dänisch",
    nor: "Norwegisch",
    gre: "Griechisch",
    tur: "Türkisch",
    chi: "Chinesisch",
    jpn: "Japanisch",
    kor: "Koreanisch",
    ara: "Arabisch",
    heb: "Hebräisch",
    hin: "Hindi",
    tha: "Thailändisch",
    vie: "Vietnamesisch",
    ind: "Indonesisch",
    per: "Persisch",
    rum: "Rumänisch",
    bul: "Bulgarisch",
    cze: "Tschechisch",
    slo: "Slowakisch",
    hun: "Ungarisch",
    est: "Estnisch",
    lav: "Lettisch",
    lit: "Litauisch",
    slv: "Slowenisch",
    mac: "Mazedonisch",
    alb: "Albanisch",
    arm: "Armenisch",
    aze: "Aserbaidschanisch",
    bel: "Weißrussisch",
    bos: "Bosnisch",
    geo: "Georgisch",
    ltz: "Luxemburgisch",
};
const FORMAT_VALUE_LABELS = {
    AA: "Audio",
    AC: "CD-Audio",
    AE: "Karte",
    BA: "Spiel",
    BB: "Hardcover",
    BC: "Softcover",
    BD: "Heft",
    BE: "Loseblattwerk",
    BF: "Ordner",
    BH: "Board Book",
    BZ: "Box",
    DA: "Kalender",
    DI: "Kartei",
    EA: "Digital",
    ED: "Download",
    PC: "Poster",
    PD: "Puzzle",
    PR: "Print",
    SA: "Schreibwaren",
    VI: "Video",
    VO: "Vinyl",
    WW: "Merchandise",
    XE: "E-Book",
    ZA: "Set",
    ZE: "Zeitschrift",
    ZJ: "Jugendbuch",
    ZZ: "Sonstiges",
};

const DEFAULT_SORT = "_text_match(buckets: 4):desc,media_boost:desc,pub_date:desc";

const SORT_OPTIONS = [
    {
        value: DEFAULT_SORT,
        label: "Relevanz (Bücher zuerst)",
    },
    {
        value: "_text_match:desc,media_boost:desc,pub_date:desc",
        label: "Relevanz",
    },
    {
        value: "media_boost:desc,_text_match:desc,pub_date:desc",
        label: "Bücher bevorzugen",
    },
    { value: "pub_date:desc", label: "Erscheinungsdatum absteigend" },
    { value: "pub_date:asc", label: "Erscheinungsdatum aufsteigend" },
    { value: "title:asc", label: "Titel A–Z" },
    { value: "title:desc", label: "Titel Z–A" },
];

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];
const FACET_PREVIEW_COUNT = 5;
const CONTENT_MAX_WIDTH = 1400;
const AUTOCOMPLETE_MIN_CHARS = 3;
const AUTOCOMPLETE_LIMIT = 6;

function escapeTypesenseFilterValue(value) {
    const s = String(value);
    if (s === "") {
        return "``";
    }
    if (/[\s,[\]`]/.test(s)) {
        return "`" + s.replace(/\\/g, "\\\\").replace(/`/g, "``") + "`";
    }
    return s;
}

function buildFilterBy(selected, excludedFields = []) {
    const parts = [];
    Object.entries(selected).forEach(([field, values]) => {
        if (excludedFields.includes(field)) {
            return;
        }
        if (!values || values.length === 0) {
            return;
        }
        if (values.length === 1) {
            parts.push(`${field}:=${escapeTypesenseFilterValue(values[0])}`);
        } else {
            parts.push(
                `${field}:[${values.map((v) => escapeTypesenseFilterValue(v)).join(",")}]`
            );
        }
    });
    return parts.join("&&");
}

function mergeCatalogFacetCounts(body, catalogBody) {
    const catalogFacet = (catalogBody?.facet_counts || []).find(
        (facet) => facet.field_name === "catalog"
    );
    if (!catalogFacet) {
        return body;
    }

    const nextFacetCounts = (body?.facet_counts || []).filter(
        (facet) => facet.field_name !== "catalog"
    );
    nextFacetCounts.unshift(catalogFacet);

    return {
        ...body,
        facet_counts: nextFacetCounts,
    };
}

function buildCatalogCounts(counts, selected) {
    const byValue = new Map();
    counts.forEach((count) => {
        byValue.set(count.value, count);
    });

    Object.keys(CATALOG_VALUE_LABELS).forEach((value) => {
        if (!byValue.has(value)) {
            byValue.set(value, { value, count: 0 });
        }
    });

    selected.forEach((value) => {
        if (!byValue.has(value)) {
            byValue.set(value, { value, count: 0 });
        }
    });

    return Array.from(byValue.values());
}

function exactIsbnFilter(isbn) {
    const values = Array.from(new Set([isbn, formatISBN(isbn)].filter(Boolean)));
    if (values.length === 1) {
        return `isbn:=${escapeTypesenseFilterValue(values[0])}`;
    }
    return `isbn:[${values.map((value) => escapeTypesenseFilterValue(value)).join(",")}]`;
}

/** ISBN: exact filter instead of fuzzy text search. */
function setTypesenseQueryFields(params, rawQuery) {
    const trimmed = String(rawQuery || "").trim();
    const isbn = isbnDigits(trimmed);
    if (isbn) {
        params.set("q", "*");
        params.set("query_by", "title");
        return exactIsbnFilter(isbn);
    }

    params.set("q", trimmed);
    params.set("query_by", QUERY_BY);
    params.set("query_by_weights", QUERY_BY_WEIGHTS);
    return "";
}

async function fetchTypesenseJson(params, signal) {
    const res = await apiFetch(`/app/typesense/books/search?${params.toString()}`, {
        method: "GET",
        signal,
        headers: {
            Accept: "application/json",
        },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(
            body.message || body.msg || `Suche fehlgeschlagen (${res.status})`
        );
    }
    return body;
}

function autocompleteLabel(hit) {
    const doc = hit?.document || {};
    const authors = Array.isArray(doc.authors)
        ? doc.authors.filter(Boolean).join(", ")
        : doc.authors || "";
    return {
        key: doc.id || `${doc.title}-${authors}-${doc.publisher || ""}`,
        title: doc.title || "",
        meta: [authors, doc.publisher].filter(Boolean).join(" | "),
    };
}

export default function SearchTypesense() {
    const [input, setInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(24);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT);
    const [facetSelection, setFacetSelection] = useState(DEFAULT_FACET_SELECTION);
    const [facetSearchTerms, setFacetSearchTerms] = useState({});
    const [expandedFacets, setExpandedFacets] = useState({});
    const [loading, setLoading] = useState(false);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [wasSearched, setWasSearched] = useState(false);
    const suggestionRequestRef = useRef(0);
    const blurTimeoutRef = useRef(null);
    const searchRequestRef = useRef(0);
    const searchAbortRef = useRef(null);

    const filterBy = useMemo(() => buildFilterBy(facetSelection), [facetSelection]);

    const runSearch = useCallback(
        async (opts = {}) => {
            const q = opts.q !== undefined ? opts.q : searchTerm;
            const p = opts.page !== undefined ? opts.page : page;
            const pp = opts.perPage !== undefined ? opts.perPage : perPage;
            const sort = opts.sortBy !== undefined ? opts.sortBy : sortBy;
            const fb = opts.filterBy !== undefined ? opts.filterBy : filterBy;
            const selectedFacets = opts.facetSelection || facetSelection;

            const trimmed = String(q || "").trim();
            if (trimmed.length < 2 && !isbnDigits(trimmed)) {
                setError("Bitte mindestens 2 Zeichen eingeben (oder eine gültige ISBN).");
                return;
            }

            const requestId = ++searchRequestRef.current;
            if (searchAbortRef.current) {
                searchAbortRef.current.abort();
            }
            const controller = new AbortController();
            searchAbortRef.current = controller;

            setLoading(true);
            setShowSuggestions(false);
            setError(null);
            setWasSearched(true);

            const params = new URLSearchParams();
            const isbnFilter = setTypesenseQueryFields(params, trimmed);
            params.set("sort_by", sort);
            params.set("per_page", String(pp));
            params.set("page", String(p));
            const nextFilterBy = [fb, isbnFilter].filter(Boolean).join("&&");
            if (nextFilterBy) {
                params.set("filter_by", nextFilterBy);
            }
            params.set("facet_by", FACET_BY);
            params.set("max_facet_values", String(MAX_FACET_VALUES));

            const catalogParams = new URLSearchParams();
            const catalogIsbnFilter = setTypesenseQueryFields(catalogParams, trimmed);
            catalogParams.set("per_page", "1");
            catalogParams.set("page", "1");
            catalogParams.set("facet_by", "catalog");
            catalogParams.set("max_facet_values", String(MAX_FACET_VALUES));
            const catalogFilterBy = [
                buildFilterBy(selectedFacets, ["catalog"]),
                catalogIsbnFilter,
            ].filter(Boolean).join("&&");
            if (catalogFilterBy) {
                catalogParams.set("filter_by", catalogFilterBy);
            }

            try {
                const [body, catalogBody] = await Promise.all([
                    fetchTypesenseJson(params, controller.signal),
                    fetchTypesenseJson(catalogParams, controller.signal),
                ]);
                if (requestId !== searchRequestRef.current) {
                    return;
                }
                setData(mergeCatalogFacetCounts(body, catalogBody));
                if (body.page != null) {
                    setPage(Number(body.page));
                }
            } catch (e) {
                if (e.name === "AbortError" || requestId !== searchRequestRef.current) {
                    return;
                }
                console.error(e);
                setError(e.message || "Unbekannter Fehler");
                setData(null);
            } finally {
                if (requestId === searchRequestRef.current) {
                    setLoading(false);
                }
            }
        },
        [searchTerm, page, perPage, sortBy, filterBy, facetSelection]
    );

    useEffect(() => {
        const term = input.trim();
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }
        if (term.length < AUTOCOMPLETE_MIN_CHARS) {
            setSuggestions([]);
            setSuggestLoading(false);
            setShowSuggestions(false);
            return;
        }

        const requestId = ++suggestionRequestRef.current;
        const timeoutId = setTimeout(async () => {
            setSuggestLoading(true);
            try {
                const params = new URLSearchParams();
                const isbnFilter = setTypesenseQueryFields(params, term);
                params.set(
                    "sort_by",
                    "_text_match:desc,media_boost:desc,pub_date:desc"
                );
                params.set("per_page", String(AUTOCOMPLETE_LIMIT));
                params.set("page", "1");
                if (isbnFilter) {
                    params.set("filter_by", isbnFilter);
                }
                const body = await fetchTypesenseJson(params);
                if (requestId !== suggestionRequestRef.current) {
                    return;
                }

                const seen = new Set();
                const nextSuggestions = (body.hits || [])
                    .map(autocompleteLabel)
                    .filter((item) => {
                        if (!item.title) return false;
                        const key = `${item.title}|${item.meta}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });

                setSuggestions(nextSuggestions);
                setShowSuggestions(nextSuggestions.length > 0);
            } catch (e) {
                if (requestId === suggestionRequestRef.current) {
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            } finally {
                if (requestId === suggestionRequestRef.current) {
                    setSuggestLoading(false);
                }
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [input]);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
            if (searchAbortRef.current) {
                searchAbortRef.current.abort();
            }
        };
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        const q = input.trim();
        setSearchTerm(q);
        runSearch({
            q,
            page: 1,
            filterBy: buildFilterBy(facetSelection),
            facetSelection,
        });
    };

    const resetSearch = () => {
        setInput("");
        setSearchTerm("");
        setPage(1);
        setFacetSelection(DEFAULT_FACET_SELECTION);
        setFacetSearchTerms({});
        setExpandedFacets({});
        setData(null);
        setError(null);
        setWasSearched(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSuggestLoading(false);
    };

    const submitSearch = (q) => {
        const nextQuery = q.trim();
        if (!nextQuery) {
            return;
        }
        setPage(1);
        setInput(nextQuery);
        setSearchTerm(nextQuery);
        setShowSuggestions(false);
        runSearch({
            q: nextQuery,
            page: 1,
            filterBy: buildFilterBy(facetSelection),
            facetSelection,
        });
    };

    const toggleFacet = (field, value) => {
        setFacetSelection((prev) => {
            const cur = prev[field] ? [...prev[field]] : [];
            const i = cur.indexOf(value);
            if (i >= 0) {
                cur.splice(i, 1);
            } else {
                cur.push(value);
            }
            const next = { ...prev };
            if (cur.length) {
                next[field] = cur;
            } else {
                delete next[field];
            }
            return next;
        });
    };

    const setFacetSearch = (field, value) => {
        setFacetSearchTerms((prev) => ({ ...prev, [field]: value }));
    };

    const toggleFacetExpanded = (field) => {
        setExpandedFacets((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    useEffect(() => {
        if (!wasSearched || !String(searchTerm).trim()) {
            return;
        }
        setPage(1);
        runSearch({
            page: 1,
            filterBy: buildFilterBy(facetSelection),
            facetSelection,
        });
    }, [facetSelection]);

    const found = data?.found ?? 0;
    const hits = data?.hits || [];
    const facetCounts = (data?.facet_counts || []).slice().sort((a, b) => {
        const aIndex = FACET_ORDER.indexOf(a.field_name);
        const bIndex = FACET_ORDER.indexOf(b.field_name);
        const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return safeAIndex - safeBIndex;
    });
    const start = found === 0 ? 0 : (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, found);
    const totalPages = Math.max(1, Math.ceil(found / perPage));

    return (
        <BasicPage title="Meta-Suche" icon={<SearchIcon />}>
            <Box
                sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    width: "60%",
                    mx: "auto",
                }}
            >
                <Typography variant="body1">
                    Sie konnten Ihren Titel nicht finden? Dann nutzen Sie bitte das{" "}
                    <Link to="/web/orderservice">Kontaktformular</Link>, bestenfalls unter Angabe einer ISBN.
                    Gerne bestellen wir nach Absprache auch antiquarisch. Wir finden, was Sie suchen!
                </Typography>
            </Box>

            <Box sx={{ mt: 2, p: 2, borderRadius: 1, width: "100%", maxWidth: CONTENT_MAX_WIDTH, mx: "auto" }}>
                <form onSubmit={onSubmit}>
                    <Box className="sui-search-box__wrapper" sx={{ position: "relative" }}>
                        <TextField
                            fullWidth
                            label="Suche nach Titel, Autor, ISBN, ..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            InputProps={{
                                endAdornment: input ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="Suche zuruecksetzen"
                                            edge="end"
                                            size="small"
                                            onClick={resetSearch}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            onFocus={() => {
                                if (suggestions.length > 0) {
                                    setShowSuggestions(true);
                                }
                            }}
                            onBlur={(e) => {
                                const term = e.target.value.trim();
                                const d = isbnDigits(term);
                                if (d && term.replace(/\D/g, "") === d) {
                                    setInput(d);
                                }
                                blurTimeoutRef.current = setTimeout(() => {
                                    setShowSuggestions(false);
                                }, 150);
                            }}
                        />
                        {showSuggestions && (
                            <Paper
                                elevation={3}
                                sx={{
                                    position: "absolute",
                                    top: "calc(100% + 6px)",
                                    left: 0,
                                    right: 0,
                                    zIndex: 20,
                                    overflow: "hidden",
                                    borderRadius: 1,
                                }}
                            >
                                {suggestLoading && (
                                    <Box sx={{ px: 2, py: 1 }}>
                                        <LinearProgress />
                                    </Box>
                                )}
                                {!suggestLoading &&
                                    suggestions.map((suggestion) => (
                                        <Box
                                            key={suggestion.key}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                submitSearch(suggestion.title);
                                            }}
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                cursor: "pointer",
                                                borderBottom: "1px solid #f0f0f0",
                                                "&:last-child": {
                                                    borderBottom: "none",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#f8f8f8",
                                                },
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {suggestion.title}
                                            </Typography>
                                            {suggestion.meta && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {suggestion.meta}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                            </Paper>
                        )}
                        <Box sx={{ height: 4, mt: 1 }}>
                            {loading && <LinearProgress />}
                        </Box>
                    </Box>
                </form>
            </Box>

            {error && (
                <Box sx={{ width: "100%", maxWidth: CONTENT_MAX_WIDTH, mx: "auto", mt: 2 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}

            <Box
                className="sui-layout"
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: 2, md: 4 },
                    width: "100%",
                    maxWidth: CONTENT_MAX_WIDTH,
                    mx: "auto",
                    mt: 2,
                    alignItems: "flex-start",
                }}
            >
                {wasSearched && (
                    <Box
                        className="sui-layout-sidebar"
                        sx={{
                            width: { xs: "100%", md: 280 },
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 1,
                                mb: 2,
                                width: "100%",
                            }}
                        >
                            <Typography
                                component="label"
                                htmlFor="typesense-sort"
                                variant="body2"
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                Sortieren nach
                            </Typography>
                            <select
                                id="typesense-sort"
                                style={{
                                    width: "100%",
                                    height: 36,
                                    margin: 0,
                                    padding: "0 32px 0 12px",
                                    border: "1px solid #c7c7c7",
                                    borderRadius: 0,
                                    backgroundColor: "#fff",
                                }}
                                value={sortBy}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSortBy(v);
                                    if (wasSearched && searchTerm) {
                                        setPage(1);
                                        runSearch({ sortBy: v, page: 1 });
                                    }
                                }}
                            >
                                {SORT_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </Box>

                        {facetCounts.map((fc) => {
                            const label = FACET_LABELS[fc.field_name] || fc.field_name;
                            const selected = facetSelection[fc.field_name] || [];
                            const searchValue = facetSearchTerms[fc.field_name] || "";
                            const normalizedSearchValue = searchValue.trim().toLowerCase();
                            const baseCounts = fc.field_name === "catalog"
                                ? buildCatalogCounts(fc.counts || [], selected)
                                : fc.counts || [];
                            const cleanedCounts = baseCounts.filter(
                                (c) =>
                                    c.value !== "false" &&
                                    c.value !== "" &&
                                    c.value !== "keine Angabe"
                            );
                            const getFacetDisplayValue = (value) => {
                                if (fc.field_name === "format") {
                                    return FORMAT_VALUE_LABELS[value] || value;
                                }
                                if (fc.field_name === "lang") {
                                    return LANGUAGE_VALUE_LABELS[value] || value;
                                }
                                if (fc.field_name === "catalog") {
                                    return CATALOG_VALUE_LABELS[value] || value;
                                }
                                return value;
                            };
                            const filteredCounts = normalizedSearchValue
                                ? cleanedCounts.filter((c) =>
                                      String(getFacetDisplayValue(c.value))
                                          .toLowerCase()
                                          .includes(normalizedSearchValue)
                                  )
                                : cleanedCounts;
                            const isExpanded = !!expandedFacets[fc.field_name];
                            const visibleCounts =
                                normalizedSearchValue || isExpanded
                                    ? filteredCounts
                                    : filteredCounts.slice(0, FACET_PREVIEW_COUNT);
                            const canExpand =
                                !normalizedSearchValue &&
                                filteredCounts.length > FACET_PREVIEW_COUNT;

                            return (
                                <fieldset key={fc.field_name} className="sui-facet">
                                    <legend className="sui-facet__title">{label}</legend>
                                    <div className="sui-facet__search">
                                        <input
                                            type="text"
                                            placeholder={`Filter ${label}`}
                                            value={searchValue}
                                            onChange={(e) =>
                                                setFacetSearch(fc.field_name, e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="sui-multi-checkbox-facet">
                                        {visibleCounts.map((c) => (
                                            <label
                                                key={`${fc.field_name}-${c.value}`}
                                                htmlFor={`ts_facet_${fc.field_name}_${c.value}`}
                                                className="sui-multi-checkbox-facet__option-label"
                                            >
                                                <div className="sui-multi-checkbox-facet__option-input-wrapper">
                                                    <input
                                                        id={`ts_facet_${fc.field_name}_${c.value}`}
                                                        type="checkbox"
                                                        className="sui-multi-checkbox-facet__checkbox"
                                                        checked={selected.includes(c.value)}
                                                        onChange={() =>
                                                            toggleFacet(fc.field_name, c.value)
                                                        }
                                                    />
                                                    <span className="sui-multi-checkbox-facet__input-text">
                                                        {getFacetDisplayValue(c.value)}
                                                    </span>
                                                </div>
                                                <span className="sui-multi-checkbox-facet__option-count">
                                                    {Number(c.count).toLocaleString("de-DE")}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    {canExpand && (
                                        <button
                                            type="button"
                                            className="sui-facet-view-more"
                                            onClick={() => toggleFacetExpanded(fc.field_name)}
                                            aria-label="Show more options"
                                        >
                                            {isExpanded ? "weniger" : "+ mehr"}
                                        </button>
                                    )}
                                </fieldset>
                            );
                        })}
                    </Box>
                )}

                <Box
                    className="sui-layout-main"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        backgroundColor: "#fafafa",
                        border: "1px solid #f0f0f0",
                        borderRadius: 1,
                        p: { xs: 2, md: 3 },
                        minHeight: wasSearched ? 0 : 320,
                    }}
                >
                    {wasSearched && !loading && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", md: "center" },
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <div className="sui-paging-info">
                                Ergebnisse{" "}
                                <strong>
                                    {start} - {end}
                                </strong>{" "}
                                von <strong>{found.toLocaleString("de-DE")}</strong>
                                {searchTerm && (
                                    <>
                                        {" "}
                                        für: <em>{searchTerm}</em>
                                    </>
                                )}
                            </div>
                            <div className="sui-results-per-page">
                                <label
                                    className="sui-results-per-page__label"
                                    htmlFor="ts-results-per-page"
                                >
                                    Ergebnisse pro Seite
                                </label>
                                <select
                                    id="ts-results-per-page"
                                    className="sui-results-per-page__select"
                                    value={perPage}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        setPerPage(v);
                                        setPage(1);
                                        if (searchTerm) {
                                            runSearch({ perPage: v, page: 1 });
                                        }
                                    }}
                                >
                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Box>
                    )}

                    {wasSearched && !loading && found === 0 && !error && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Keine Ergebnisse gefunden.
                        </Alert>
                    )}

                    {wasSearched &&
                        !loading &&
                        hits.map((hit) => (
                            <ResultViewTypesense
                                key={
                                    hit.document?.id ??
                                    hit.document?.isbn ??
                                    `${hit.document?.title}-${hit.document?.publisher}`
                                }
                                document={hit.document}
                                highlights={hit.highlights}
                            />
                        ))}

                    {wasSearched && !loading && found > perPage && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                my: 3,
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                siblingCount={2}
                                boundaryCount={1}
                                shape="rounded"
                                onChange={(_, nextPage) => {
                                    setPage(nextPage);
                                    runSearch({ page: nextPage });
                                }}
                                renderItem={(item) => (
                                    <PaginationItem
                                        {...item}
                                        sx={{
                                            fontSize: 18,
                                            minWidth: 44,
                                            height: 44,
                                            borderRadius: item.type === "previous" || item.type === "next" ? 1 : 0,
                                            border:
                                                item.type === "previous" || item.type === "next"
                                                    ? "1px solid #d9d9d9"
                                                    : "none",
                                            color: item.selected ? "text.primary" : "primary.main",
                                            fontWeight: item.selected ? 700 : 400,
                                            bgcolor: item.selected ? "transparent" : "transparent",
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {!wasSearched && (
                        <Box
                            sx={{
                                minHeight: 260,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </BasicPage>
    );
}
